import React, { ChangeEvent, FC, useContext, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  useTheme,
} from '@mui/material';
import { IReviewComment } from '../../shared/interfaces/IReviewComment';
import { AuthContext } from '../../providers/AuthProvider';
import CommentingService from '../../services/CommentingService';
import CommentAiService from '../../services/CommentAiService';
import { constants } from '../../constants/constants';
import { CommunityManagement } from '../../shared/interfaces/ICommunityManagement';
import { useTranslation } from 'react-i18next';

interface Props {
  reviewId: string;
  input: string;
  username?: string | null;
  reviewComment?: IReviewComment;
  communityManagement: CommunityManagement;
}

const ReviewReply: FC<Props> = ({
  reviewId,
  input,
  username,
  reviewComment,
  communityManagement,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasRole } = useContext(AuthContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [reply, setReply] = useState<IReviewComment>(reviewComment || { text: '', status: 'OPEN' });
  const [deleted, setDeleted] = useState<boolean>(false);
  const [language, setLanguage] = useState<Language | ''>('de');

  const handleGenerateReply = async () => {
    const data = await CommentAiService.generateComment(
      language,
      constants.tonality[communityManagement.tonality.preference],
      communityManagement.pronoun.preference === 0,
      input,
      setLoading
    );

    if (!data) return;

    setReply((prevState) => ({
      ...prevState,
      text:
        communityManagement.salutationPrefix +
        (communityManagement.useUsername && username ? ' ' + username : '') +
        '\n\n' +
        data +
        '\n\n' +
        communityManagement.greeting,
    }));
  };

  const handleSaveReply = async () => {
    const data = await CommentingService.saveReviewComment(
      reviewId,
      reply.status,
      reply.text,
      setLoading
    );

    if (!data) return;
    setDeleted(false);
  };

  const handleDeleteReply = async () => {
    const data = await CommentingService.deleteReviewComment(reviewId, setLoading);

    if (!data) return;

    setReply({ text: '', status: '' });
    setDeleted(true);
  };

  return (
    <Box sx={{ mt: theme.spacing(2) }}>
      <TextField
        disabled={loading}
        fullWidth
        label="Kommentar"
        multiline
        rows={8}
        InputLabelProps={{ shrink: !!reply.text }}
        value={reply.text}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setReply((prevState) => ({
            ...prevState,
            text: event.target.value,
          }));
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', my: theme.spacing(2) }}>
        {hasRole('can-use-ai') && (
          <Button
            disabled={loading || !input}
            variant="contained"
            onClick={async () => await handleGenerateReply()}
            sx={{ mr: theme.spacing(1) }}
          >
            {loading ? (
              <CircularProgress
                sx={{ ml: theme.spacing(1) }}
                size={24}
              />
            ) : (
              t('generate')
            )}
          </Button>
        )}
        <Button
          disabled={loading}
          variant="contained"
          onClick={async () => await handleSaveReply()}
        >
          {loading && !reply.text ? (
            <CircularProgress
              sx={{ ml: theme.spacing(1) }}
              size={24}
            />
          ) : (
            t('save')
          )}
        </Button>
        <Button
          disabled={loading}
          variant="contained"
          color="warning"
          onClick={() => {
            setReply({ text: '', status: '' });
          }}
          sx={{ ml: 1 }}
        >
          {t('reset')}
        </Button>
        <Button
          disabled={loading || !reviewComment?.text || !reviewComment?.status || deleted}
          variant="contained"
          color="error"
          onClick={async () => await handleDeleteReply()}
          sx={{ ml: 1 }}
        >
          {t('delete')}
        </Button>
        <FormControl
          size="small"
          fullWidth
          sx={{ minWidth: 300, maxWidth: 300, ml: 'auto' }}
        >
          <InputLabel id="language-label">{t('language')}</InputLabel>
          <Select
            labelId="language-label"
            label={t('language')}
            value={language}
            onChange={(e: SelectChangeEvent) => {
              setLanguage(e.target.value as Language);
            }}
          >
            <MenuItem value="de">{t('german')}</MenuItem>
            <MenuItem value="en">{t('english')}</MenuItem>
            <MenuItem value="nl">{t('dutch')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl
          disabled={loading}
          size="small"
          sx={{ minWidth: '200px', ml: 'auto' }}
        >
          <InputLabel id="status-select-label">{t('status')}</InputLabel>
          <Select
            labelId="status-select-label"
            value={reply.status || 'OPEN'}
            onChange={(event: SelectChangeEvent) => {
              setReply((prevState) => ({
                ...prevState,
                status: event.target.value,
              }));
            }}
            label={t('status')}
          >
            <MenuItem value="">
              <em>-</em>
            </MenuItem>
            <MenuItem value="OPEN">{t('OPEN')}</MenuItem>
            <MenuItem value="APPROVAL">{t('APPROVAL')}</MenuItem>
            <MenuItem value="DONE">{t('DONE')}</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default ReviewReply;
